import React, { useState, useEffect } from 'react';

const API = 'https://groovie-business-api-production.up.railway.app';

const Icons = {
  users: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  trending: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/><polyline points="17,6 23,6 23,12"/></svg>,
  signal: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h.01M7 20v-4M12 20v-8M17 20V8M22 20V4"/></svg>,
  chart: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  refresh: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
};

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [timeRange, setTimeRange] = useState(7);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const companyId = (() => {
    try { return JSON.parse(atob(token.split('.')[1])).company_id; }
    catch(e) { return null; }
  })();
  const headers = { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
  var f = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";

  useEffect(function() { fetchAll(); }, [selectedChannel, timeRange]);

  var fetchAll = async function() {
    setLoading(true);
    try {
      var ovRes = await fetch(API + '/api/v1/analytics/' + companyId + '/overview', { headers: headers });
      var chRes = await fetch(API + '/api/v1/analytics/' + companyId + '/channels', { headers: headers });
      var ovData = await ovRes.json();
      var chData = await chRes.json();
      setOverview(ovData);
      setChannels(chData.channels || []);

      var channelParam = selectedChannel ? '&channel_id=' + selectedChannel : '';
      var hrRes = await fetch(API + '/api/v1/analytics/' + companyId + '/hourly?days=' + timeRange + channelParam, { headers: headers });
      var dyRes = await fetch(API + '/api/v1/analytics/' + companyId + '/daily?days=' + timeRange + channelParam, { headers: headers });
      setHourly((await hrRes.json()).hourly || []);
      setDaily((await dyRes.json()).daily || []);
    } catch (e) { console.error('Analytics fetch error:', e); }
    setLoading(false);
  };

  var maxHourly = Math.max.apply(null, hourly.map(function(h) { return h.peak_listeners || 0; }).concat([1]));
  var maxDaily = Math.max.apply(null, daily.map(function(d) { return d.peak_listeners || 0; }).concat([1]));

  var selectStyle = {
    padding: '8px 14px', borderRadius: 10, border: '1px solid #e5e7eb',
    background: '#fff', fontSize: 13, fontFamily: f, color: '#374151',
    outline: 'none', cursor: 'pointer',
  };

  return (
    <div style={{ padding: '28px 32px', fontFamily: f }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#7c3aed' }}>{Icons.chart}</span>
            Analytics
          </h1>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Listener data from AzuraCast webhook integration</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={selectedChannel} onChange={function(e) { setSelectedChannel(e.target.value); }} style={selectStyle}>
            <option value="">All Channels</option>
            {channels.map(function(ch) { return <option key={ch.channel_id} value={ch.channel_id}>{ch.channel_name}</option>; })}
          </select>
          <select value={timeRange} onChange={function(e) { setTimeRange(Number(e.target.value)); }} style={selectStyle}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button onClick={fetchAll} style={{
            padding: '8px 14px', borderRadius: 10, border: '1px solid #e5e7eb',
            background: '#fff', cursor: 'pointer', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, fontFamily: f,
          }}>{Icons.refresh} Refresh</button>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: Icons.users, label: 'Current Listeners', value: overview ? overview.current_listeners : '—', color: '#7c3aed' },
          { icon: Icons.trending, label: "Today's Peak", value: overview ? overview.today_peak : '—', color: '#059669' },
          { icon: Icons.signal, label: 'Active Channels', value: overview ? overview.active_channels : '—', color: '#2563eb' },
          { icon: Icons.chart, label: 'Total Snapshots', value: overview ? overview.total_snapshots : '—', color: '#d97706', sub: 'Data points collected' },
        ].map(function(stat, i) {
          return (
            <div key={i} style={{
              background: '#fff', borderRadius: 16, padding: '20px 22px',
              border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: stat.color + '12', color: stat.color,
                }}>{stat.icon}</div>
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>{stat.label}</span>
              </div>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-1px' }}>{stat.value}</p>
              {stat.sub && <p style={{ fontSize: 11, color: '#d1d5db', margin: '4px 0 0' }}>{stat.sub}</p>}
            </div>
          );
        })}
      </div>

      {/* Channel Breakdown */}
      {channels.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 16, padding: '22px 24px', marginBottom: 24,
          border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Channel Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {channels.map(function(ch) {
              return (
                <div key={ch.channel_id} style={{
                  background: '#fafafa', borderRadius: 12, padding: '14px 18px',
                  border: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>{ch.channel_name}</p>
                    <p style={{ fontSize: 11, color: '#d1d5db', margin: '2px 0 0' }}>Peak today: {ch.peak_today || 0}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>{ch.current_listeners || 0}</p>
                    <p style={{ fontSize: 10, color: '#d1d5db', margin: 0 }}>now</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hourly Chart */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: '22px 24px', marginBottom: 24,
        border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Listeners by Hour of Day</h3>
        {hourly.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>No hourly data yet. Data appears once AzuraCast webhooks are active and listeners connect.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 180 }}>
            {hourly.map(function(h, i) {
              var pct = ((h.peak_listeners || 0) / maxHourly) * 100;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 9, color: '#9ca3af' }}>{h.peak_listeners || 0}</span>
                  <div style={{
                    width: '100%', borderRadius: '4px 4px 0 0', minHeight: 2,
                    height: pct + '%',
                    background: 'linear-gradient(to top, #7c3aed, #a855f7)',
                    transition: 'height 0.3s ease',
                  }} />
                  <span style={{ fontSize: 9, color: '#d1d5db' }}>{String(h.hour).padStart(2, '0')}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily Chart */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: '22px 24px',
        border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Daily Listener Trends</h3>
        {daily.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>No daily data yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 180 }}>
            {daily.map(function(d, i) {
              var pct = ((d.peak_listeners || 0) / maxDaily) * 100;
              var dt = new Date(d.date);
              var label = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 9, color: '#9ca3af' }}>{d.peak_listeners || 0}</span>
                  <div style={{
                    width: '100%', borderRadius: '4px 4px 0 0', minHeight: 2,
                    height: pct + '%',
                    background: 'linear-gradient(to top, #2563eb, #7c3aed)',
                    transition: 'height 0.3s ease',
                  }} />
                  <span style={{ fontSize: 8, color: '#d1d5db', transform: 'rotate(-45deg)', transformOrigin: 'top left', marginTop: 4, whiteSpace: 'nowrap' }}>{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, padding: '10px 18px', borderRadius: 10,
          background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
        }}>Loading analytics...</div>
      )}
    </div>
  );
}
